import { Link } from "react-router-dom";
import { Check, ArrowRight, Search, Wind, Repeat, FlaskConical, Bug, Zap, Truck, Droplets, Home, Trees, ShieldCheck, Sun, Waves, Building2, Tent, Users, Leaf, Thermometer, CalendarDays, MapPin } from "lucide-react";
import { Reveal, MaskLines } from "./Motion";
import { Bento } from "./Bento";
import { ZonesGrid } from "./ZonesGrid";
import { SPECIES, CONSEILS_LIST, ZONES } from "../../data/site";
import { citiesOfZone } from "../../data/cities";

const ICONS = { Search, Wind, Repeat, FlaskConical, Bug, Zap, Truck, Droplets, Home, Trees, ShieldCheck, Sun, Waves, Building2, Tent, Users, Leaf, Thermometer, CalendarDays, MapPin };
const wrap = "max-w-[1400px] mx-auto px-5 lg:px-10";

const Head = ({ eyebrow, title, intro, light }) => (
  <div className="max-w-3xl">
    {eyebrow && <p className={`eyebrow ${light ? "text-lime" : "text-sage"}`}>{eyebrow}</p>}
    <h2 className={`h2 mt-5 ${light ? "text-cream" : "text-forest"}`}>{title}</h2>
    {intro && <p className={`lead-text mt-6 ${light ? "text-cream/80" : "text-ink/70"}`}>{intro}</p>}
  </div>
);

const Split = ({ b }) => (
  <section className="py-20 md:py-28">
    <div className={`${wrap} grid lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
      <Reveal className={b.reverse ? "lg:order-2" : ""}>
        <div className="relative">
          <div className={`overflow-hidden ${b.arch ? "clip-arch" : "rounded-[28px]"} aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] bg-sand`}>
            <img src={b.image} alt={b.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] hover:scale-105" />
          </div>
          {b.badge && <div className="absolute -bottom-6 left-6 right-6 sm:right-auto rounded-2xl bg-lime text-forest px-6 py-4 font-display font-extrabold text-lg shadow-xl">{b.badge}</div>}
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="eyebrow text-sage">{b.eyebrow}</p>
        <h2 className="h2 mt-5 text-forest">{b.title}</h2>
        <div className="prose-site mt-7">{b.text.map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}</div>
        {b.bullets && (
          <ul className="mt-6 space-y-3">
            {b.bullets.map(x => <li key={x} className="flex gap-3 text-[15px] text-ink/85"><span className="mt-0.5 w-6 h-6 rounded-full bg-moss text-lime grid place-items-center shrink-0"><Check className="w-3.5 h-3.5" /></span>{x}</li>)}
          </ul>
        )}
        {b.link && <Link to={b.link.to} className="btn btn-ghost mt-8">{b.link.label}<ArrowRight className="w-4 h-4" /></Link>}
      </Reveal>
    </div>
  </section>
);

const Cards = ({ b }) => (
  <section className={`py-20 md:py-28 ${b.dark ? "bg-forest grain relative" : ""}`}>
    <div className={`${wrap} relative z-[2]`}>
      <Reveal><Head eyebrow={b.eyebrow} title={b.title} intro={b.intro} light={b.dark} /></Reveal>
      <div className={`mt-14 grid gap-4 sm:grid-cols-2 ${b.items.length % 3 === 0 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
        {b.items.map((it, i) => {
          const Icon = ICONS[it.icon] || Leaf;
          return (
            <Reveal key={it.title} delay={0.05 * i} className="h-full">
              <article className={`h-full rounded-3xl p-7 border transition-transform duration-500 hover:-translate-y-1 ${b.dark ? "bg-pine border-cream/10 text-cream" : "bg-white border-moss/15"}`}>
                <span className={`w-12 h-12 rounded-2xl grid place-items-center ${b.dark ? "bg-lime text-forest" : "bg-sand text-moss"}`}><Icon className="w-5 h-5" /></span>
                <h3 className={`mt-6 font-display text-xl font-extrabold leading-tight ${b.dark ? "text-cream" : "text-forest"}`}>{it.title}</h3>
                <p className={`mt-3 text-[15px] leading-relaxed ${b.dark ? "text-cream/80" : "text-ink/70"}`}>{it.text}</p>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Steps = ({ b }) => (
  <section className="py-20 md:py-28 bg-sand/50">
    <div className={wrap}>
      <Reveal><Head eyebrow={b.eyebrow} title={b.title} intro={b.intro} /></Reveal>
      <ol className="mt-14 grid gap-px bg-moss/15 rounded-[28px] overflow-hidden md:grid-cols-2 lg:grid-cols-4 border border-moss/15">
        {b.items.map((s, i) => (
          <Reveal key={s.title} as="li" delay={0.08 * i} className="bg-cream p-8 md:p-10 h-full">
            <span className="font-serif italic text-6xl text-moss/80 leading-none">0{i + 1}</span>
            <h3 className="mt-8 font-display text-xl font-extrabold text-forest">{s.title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-ink/70">{s.text}</p>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const Communes = ({ b }) => (
  <section className="py-20 md:py-24">
    <div className={`${wrap} grid lg:grid-cols-[0.9fr_1.1fr] gap-12`}>
      <Reveal><Head eyebrow={b.eyebrow || "Communes desservies"} title={b.title} intro={b.intro} /></Reveal>
      <Reveal delay={0.1}>
        <ul className="flex flex-wrap gap-2.5" data-testid="communes-list">
          {b.items.map(c => {
            const label = <><MapPin className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5 text-sage" />{c.name || c}</>;
            const cls = "block rounded-full border border-moss/20 bg-white px-4 py-2 text-sm font-semibold text-forest hover:bg-lime transition-colors";
            return <li key={c.name || c}>{c.path ? <Link to={c.path} className={`${cls} underline-offset-4 hover:underline`} data-testid={`commune-link-${c.path.slice(1)}`}>{label}</Link> : <span className={cls}>{label}</span>}</li>;
          })}
        </ul>
      </Reveal>
    </div>
  </section>
);

const Statement = ({ b }) => (
  <section className="py-24 md:py-36">
    <div className={wrap}>
      <p className="eyebrow text-sage">{b.eyebrow}</p>
      <p className="mt-8 font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold leading-[1.05] text-forest max-w-6xl">
        <MaskLines lines={b.lines} inView delay={0} />
      </p>
      {b.text && <Reveal delay={0.2}><p className="lead-text mt-10 max-w-2xl text-ink/70">{b.text}</p></Reveal>}
    </div>
  </section>
);

const Prose = ({ b }) => (
  <section className="py-20 md:py-24">
    <div className={`${wrap} grid lg:grid-cols-[0.8fr_1.2fr] gap-10`}>
      <Reveal><p className="eyebrow text-sage">{b.eyebrow}</p><h2 className="h2 mt-5 text-forest">{b.title}</h2></Reveal>
      <Reveal delay={0.1}>
        <div className="prose-site md:columns-2 md:gap-10">{b.text.map((p, i) => <p key={i} className="break-inside-avoid" dangerouslySetInnerHTML={{ __html: p }} />)}</div>
      </Reveal>
    </div>
  </section>
);

const BentoBlock = ({ b }) => (
  <section className="py-20 md:py-28">
    <div className={wrap}>
      <Reveal><Head eyebrow={b.eyebrow || "Notre traitement"} title={b.title || "7 engagements pour un extérieur sans moustiques"} intro={b.intro} /></Reveal>
      <div className="mt-14"><Bento /></div>
    </div>
  </section>
);

const ZonesBlock = ({ b }) => (
  <section className="py-20 md:py-28">
    <div className={wrap}>
      <Reveal><Head eyebrow={b.eyebrow || "Secteurs d'intervention"} title={b.title || "Nous intervenons partout en Gironde"} intro={b.intro} /></Reveal>
      <div className="mt-14"><ZonesGrid exclude={b.exclude} /></div>
    </div>
  </section>
);

const LinksBlock = ({ b }) => {
  const items = (b.source === "conseils" ? CONSEILS_LIST : SPECIES).filter(x => x.path !== b.exclude);
  return (
    <section className="py-20 md:py-28">
      <div className={wrap}>
        <Reveal><Head eyebrow={b.eyebrow} title={b.title} intro={b.intro} /></Reveal>
        <div className={`mt-14 grid gap-4 sm:grid-cols-2 ${items.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {items.map((it, i) => (
            <Reveal key={it.path} delay={0.06 * i}>
              <Link to={it.path} className="group block overflow-hidden rounded-[28px] border border-moss/15 bg-white" data-testid={`link-card-${it.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-sand"><img src={it.image} alt={it.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" /></div>
                <div className="p-6">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sage">{it.tag}</p>
                  <h3 className="mt-2 font-display text-2xl font-extrabold text-forest flex items-center justify-between gap-2">{it.name}<ArrowRight className="w-5 h-5 text-moss transition-transform group-hover:translate-x-1" /></h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CityGroups = ({ b }) => (
  <section className="py-20 md:py-28">
    <div className={wrap}>
      <Reveal><Head eyebrow="Villes d'intervention" title={b.title} intro={b.intro} /></Reveal>
      <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="city-groups">
        {ZONES.map(z => (
          <Reveal key={z.slug} className="h-full">
            <div className="h-full rounded-3xl border border-moss/15 bg-white p-7">
              <Link to={z.path} className="font-display text-2xl font-extrabold text-forest hover:text-moss inline-flex items-center gap-2">Secteur {z.name}<ArrowRight className="w-4 h-4" /></Link>
              <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5">
                {citiesOfZone(z.slug).map(c => <li key={c.slug}><Link to={c.path} className="text-[14px] text-ink/80 hover:text-moss hover:underline underline-offset-4" data-testid={`city-link-${c.slug}`}>{c.name}</Link></li>)}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Gallery = ({ b }) => (
  <section className="py-20 md:py-28 overflow-hidden" data-testid="photo-gallery">
    <div className={wrap}>
      <Reveal><Head eyebrow={b.eyebrow} title={b.title} intro={b.intro} /></Reveal>
      <div className="mt-14 grid gap-5 md:grid-cols-3 md:items-start">
        {b.items.map((it, i) => (
          <Reveal key={it.image + i} delay={0.08 * i} className={i === 1 ? "md:mt-16" : ""}>
            <figure className="group">
              <div className={`relative overflow-hidden bg-sand ${i === 1 ? "clip-arch aspect-[4/5] md:aspect-[3/4]" : "rounded-[28px] aspect-[4/3] md:aspect-[4/5]"}`}>
                <img src={it.image} alt={it.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
                <span className="absolute top-4 left-4 rounded-full bg-cream/90 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-forest">0{i + 1}</span>
              </div>
              <figcaption className="mt-4 font-display text-lg font-bold text-forest leading-snug">{it.caption}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MAP = { split: Split, cards: Cards, steps: Steps, communes: Communes, statement: Statement, prose: Prose, bento: BentoBlock, zones: ZonesBlock, links: LinksBlock, cityGroups: CityGroups, gallery: Gallery };

export const Blocks = ({ blocks = [] }) => blocks.map((b, i) => {
  const C = MAP[b.type];
  return C ? <C key={i} b={b} /> : null;
});
